import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Trash2, X } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';

function DeleteTeamModal({
  closeModal,
  onTeamDeleted,
  name,
  managers,
  members,
  teamId,
  workspaceId,
}) {
  const { displayNotification } = useContext(NotificationContext);
  const handleDeleteTeam = async () => {
    const { success } = await sendRequest({
      method: 'delete',
      url: API.WORKSPACE.deleteTeam(workspaceId, teamId),
    });
    if (success) {
      setTimeout(() => {
        onTeamDeleted();
        closeModal();
        displayNotification({
          message: `Team “${name}” deleted.`,
          severity: 'success',
          autoHideDuration: 3000,
        });
      }, 500);
    } else {
      displayNotification({
        message: 'There was an issue deleting team.',
        severity: 'error',
      });
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c create-team-modal"
      // Rendered inside the clickable TeamCard — portal events bubble up the
      // React tree, so without this every click in the modal also clicks the card.
      onClick={(e) => e.stopPropagation()}
    >
      <Box style={{
        width: 448,
        padding: 24,
        background: '#fff',
        borderRadius: 8,
        position: 'relative',
      }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closeModal}
          className={'absolute top-4 right-4 bg-transparent border-0 p-0 text-ink-500 '
            + 'opacity-70 hover:opacity-100 transition-opacity'}
        >
          <X size={16} />
        </button>
        <div className={'flex items-center justify-center w-10 h-10 rounded-full '
          + 'bg-red-100 text-red-600 mb-2'}
        >
          <Trash2 size={18} />
        </div>
        <h5 className="text-lg font-semibold text-ink-900 mb-4">
          Are you sure to delete this team?
        </h5>
        <div className="space-y-3">
          <p className="text-sm text-ink-500 leading-relaxed mb-0">
            You&apos;re about to delete the
            {' '}
            <span className="font-semibold text-ink-900">
              &quot;
              {name}
              &quot;
            </span>
            {' '}
            and this action can not be undone.
          </p>
          <div className="space-y-1 text-sm">
            <div className="flex gap-2">
              <span className="text-ink-500 w-20">Manager:</span>
              <span className="text-ink-900 font-medium">{managers[0]?.name}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-ink-500 w-20">Members:</span>
              <span className="text-ink-900 font-medium">{members.length}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-start gap-2 mt-4">
          <button
            type="button"
            onClick={closeModal}
            className={'inline-flex items-center justify-center px-4 py-2 border border-rule '
              + 'rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition'}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteTeam}
            className={'inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white '
              + 'rounded-[8px] text-sm font-medium hover:bg-red-700 transition'}
          >
            Delete
          </button>
        </div>
      </Box>
    </Modal>
  );
}

DeleteTeamModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onTeamDeleted: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  managers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired),
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired),
  teamId: PropTypes.number.isRequired,
  workspaceId: PropTypes.number.isRequired,
};

export default DeleteTeamModal;
